/**
 * The renewal price prompt (tracker item 85, decision 15): whenever the unit
 * price of a bundle visit changes, the user chooses whether the renewal price
 * follows. Two buttons, no pre-selected default, Enter never chooses.
 *
 * `useRenewalPriceChoice().withChoice(send)` is the one way to wire it in: it
 * calls `send()` without a choice; when the server answers 400
 * RENEWAL_PRICE_CHOICE_REQUIRED it shows the dialog with the server's numbers
 * and calls `send(action)` with the pick. Cancelling rejects with
 * RenewalPriceCancelled (nothing was saved).
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import * as types from '../../api/types';
import { getErrorCode, getErrorDetails } from '../../utils/apiError';
import { formatJobMoney } from '../../utils/jobPresentation';
import { primaryButtonClass, secondaryButtonClass } from '../../components/formStyles';

/** The prompt's details when the error is RENEWAL_PRICE_CHOICE_REQUIRED, else null. */
export function renewalPriceChoiceDetails(err: unknown): types.RenewalPriceChoiceDetails | null {
  if (getErrorCode(err) !== types.BUNDLE_ERROR_CODES.renewalPriceChoiceRequired) return null;
  const details = getErrorDetails(err);
  if (!details || typeof details.oldUnitPrice !== 'string' || typeof details.newUnitPrice !== 'string') return null;
  return {
    enrollmentId: String(details.enrollmentId ?? ''),
    step: Number(details.step ?? 0),
    jobId: String(details.jobId ?? ''),
    oldUnitPrice: details.oldUnitPrice,
    newUnitPrice: details.newUnitPrice,
    renewalUnitPrice: typeof details.renewalUnitPrice === 'string' ? details.renewalUnitPrice : null,
    currentRenewalPrice: typeof details.currentRenewalPrice === 'string' ? details.currentRenewalPrice : details.oldUnitPrice,
  };
}

/** "Today $100.00 → $110.00; renewal price is currently $105.00" */
export function renewalPriceSentence(details: types.RenewalPriceChoiceDetails): string {
  return `Today ${formatJobMoney(details.oldUnitPrice)} → ${formatJobMoney(details.newUnitPrice)}; renewal price is currently ${formatJobMoney(details.currentRenewalPrice)}`;
}

export class RenewalPriceCancelled extends Error {
  constructor() {
    super('The price change was cancelled');
    this.name = 'RenewalPriceCancelled';
  }
}

export function isRenewalPriceCancelled(err: unknown): boolean {
  return err instanceof RenewalPriceCancelled;
}

interface PromptProps {
  details: types.RenewalPriceChoiceDetails;
  /** What the visit is, e.g. "Fall Aeration (2 of 3)" */
  title?: string;
  onChoose: (action: types.RenewalPriceAction) => void;
  onCancel: () => void;
}

export function RenewalPricePrompt({ details, title, onChoose, onCancel }: PromptProps) {
  const dialog = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // Focus the dialog itself, not a button: nothing is pre-selected
    const origin = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    dialog.current?.focus();
    return () => origin?.focus();
  }, []);

  const onKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Escape') { event.stopPropagation(); onCancel(); return; }
    // Enter must not choose: only a deliberate click or a button activated by the keyboard does
    if (event.key === 'Enter' && !(event.target instanceof HTMLButtonElement)) { event.preventDefault(); return; }
    if (event.key === 'Tab') {
      const focusable = Array.from(dialog.current?.querySelectorAll<HTMLElement>('button') ?? []);
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && (document.activeElement === first || document.activeElement === dialog.current)) { event.preventDefault(); last.focus(); }
      else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
    }
  };

  return (
    <div className="fixed inset-0 z-[70] flex items-end justify-center bg-black/40 p-0 sm:items-center sm:p-4" role="presentation">
      <div
        ref={dialog}
        role="dialog"
        aria-modal="true"
        aria-labelledby="renewal-price-title"
        aria-describedby="renewal-price-numbers"
        tabIndex={-1}
        onKeyDown={onKeyDown}
        className="max-h-full w-full overflow-y-auto rounded-t-lg bg-white p-5 shadow-xl focus:outline-none sm:max-w-md sm:rounded-lg"
      >
        <h2 id="renewal-price-title" className="text-lg font-semibold text-gray-900">Also update the renewal price?</h2>
        {title && <p className="mt-1 break-words text-sm text-gray-600">{title}</p>}
        <p id="renewal-price-numbers" className="mt-3 text-sm text-gray-800" data-testid="renewal-price-numbers">{renewalPriceSentence(details)}</p>
        <p className="mt-2 text-xs text-gray-500">
          The renewal is quoted at the renewal price. Choose whether it follows this change; nothing is saved until you choose.
        </p>
        <div className="mt-5 flex flex-col gap-2">
          <button type="button" className={`${primaryButtonClass} w-full`} onClick={() => onChoose('UPDATE')}>Update renewal price too</button>
          <button type="button" className={`${secondaryButtonClass} w-full`} onClick={() => onChoose('KEEP')}>Keep renewal price</button>
          <button type="button" className="py-2 text-sm text-gray-600 underline" onClick={onCancel}>Cancel the price change</button>
        </div>
      </div>
    </div>
  );
}

interface Pending {
  details: types.RenewalPriceChoiceDetails;
  title?: string;
  resolve: (action: types.RenewalPriceAction | null) => void;
}

export interface RenewalPriceChoice {
  /**
   * Runs `send` once without a choice; on RENEWAL_PRICE_CHOICE_REQUIRED shows the prompt and runs it again
   * with the pick. Throws RenewalPriceCancelled when the user cancels; every other error is rethrown.
   */
  withChoice<T>(send: (action?: types.RenewalPriceAction) => Promise<T>, title?: string): Promise<T>;
  /** Render this once in the page */
  dialog: React.ReactNode;
}

export function useRenewalPriceChoice(): RenewalPriceChoice {
  const [pending, setPending] = useState<Pending | null>(null);
  const alive = useRef(true);
  const open = useRef<Pending | null>(null);

  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
      open.current?.resolve(null);
    };
  }, []);

  const withChoice = useCallback(async function withChoice<T>(send: (action?: types.RenewalPriceAction) => Promise<T>, title?: string): Promise<T> {
    try {
      return await send(undefined);
    } catch (err) {
      const details = renewalPriceChoiceDetails(err);
      if (!details) throw err;
      const action = await new Promise<types.RenewalPriceAction | null>((resolve) => {
        const entry: Pending = { details, title, resolve };
        open.current = entry;
        setPending(entry);
      });
      open.current = null;
      if (alive.current) setPending(null);
      if (!action) throw new RenewalPriceCancelled();
      return send(action);
    }
  }, []);

  const dialog = pending ? (
    <RenewalPricePrompt details={pending.details} title={pending.title} onChoose={(action) => pending.resolve(action)} onCancel={() => pending.resolve(null)} />
  ) : null;

  return { withChoice, dialog };
}
